import { useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { useSession } from '../SessionContext';
import config from '../config';
import { IUserSession } from '../interfaces/IUserSession';
import { applyRefreshToSession } from '../utils/refreshSession';

export function useProactiveRefresh() {
    const { session, setSession } = useSession<IUserSession>();
    const navigate = useNavigate();

    useEffect(() => {
        if (!session?.token) return;

        try {
            const { exp } = JSON.parse(atob(session.token.split('.')[1]));
            const msUntilExpiry = exp * 1000 - Date.now();
            const refreshBeforeMs = msUntilExpiry - 60_000; // 1 minute before expiry

            if (refreshBeforeMs <= 0) return; // already expired or too close

            const timerId = setTimeout(async () => {
                try {
                    const resp = await axios.post(
                        `${config.managementBaseUrl}/users/refresh`,
                        null,
                        {
                            withCredentials: true,
                            headers: {
                                Authorization: `Bearer ${session?.token}`
                            }
                        }
                    );
                    setSession(applyRefreshToSession(session, resp.data));
                } catch (err: unknown) {
                    const status = err instanceof AxiosError ? err.response?.status : undefined;
                    const isTerminal = status === 400 || status === 401 || status === 403;
                    if (isTerminal) {
                        // Server confirmed the session is invalid — log out.
                        console.warn('[Auth] Proactive refresh rejected by server, logging out', { status });
                        setSession(null);
                        navigate('/');
                    } else {
                        // Transient failure (network, timeout, 5xx) — let the token expire naturally;
                        // the 401 interceptor will trigger a fresh refresh on the next API call.
                        console.warn('[Auth] Proactive refresh failed (transient), will retry via interceptor', err instanceof Error ? err.message : String(err));
                    }
                }
            }, refreshBeforeMs);

            return () => clearTimeout(timerId);
        } catch (err) {
            console.error('Invalid token format', err);
        }
    }, [session, setSession, navigate]);
}

export function ProactiveRefreshBridge() {
    useProactiveRefresh();
    return null;
}